import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import ComparisonTable from "@/components/ComparisonTable";
import ProductCard from "@/components/ProductCard";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import EmailCapture from "@/components/EmailCapture";
import SEOHead from "@/components/SEOHead";
import TableOfContents from "@/components/TableOfContents";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

const TOC = [
  { id: "comparison", label: "Quick Comparison Table" },
  { id: "reviews", label: "Detailed Reviews by Category" },
  { id: "how-we-picked", label: "How We Picked These Panels" },
  { id: "what-efficiency-means", label: "What High Efficiency Actually Means" },
  { id: "technology", label: "Which Technology to Choose" },
  { id: "related", label: "Related Guides" },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Best High-Efficiency Solar Panels (2026)",
  "description": "Compare the best high-efficiency solar panels across every category. Expert picks for RVs, camping, solar generators, portable use, and permanent installations.",
  "url": "https://highestefficiencysolarcell.com/best-high-efficiency-solar-panels",
  "dateModified": "2026-06-01",
  "author": { "@type": "Organization", "name": "HighestEfficiencySolarCell.com" },
};

export default function BestHighEfficiencyPanels() {
  const { data: products = [] } = useQuery({
    queryKey: ["solar-products"],
    queryFn: () => base44.entities.SolarProduct.list("sort_order", 50),
  });

  return (
    <>
      <SEOHead
        title="Best High-Efficiency Solar Panels 2026 | Top Picks for Every Use Case"
        description="Compare the best high-efficiency solar panels by category. Expert picks for RVs, camping, solar generators, portable use, and permanent rooftop installations."
        canonical="https://highestefficiencysolarcell.com/best-high-efficiency-solar-panels"
        schema={SCHEMA}
      />
      <div className="pb-28 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">Best High-Efficiency Solar Panels</span>
          </nav>

          <div className="max-w-4xl">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
              Best High-Efficiency Solar Panels (2026)
            </h1>
            <p className="text-lg text-muted-foreground mb-4 max-w-3xl">
              We compared the top high-efficiency solar panels across every category — from portable foldable panels
              for camping and backpacking to premium rigid panels for permanent rooftop and RV installations. Every panel on this list is available on Amazon and represents the best we found in its specific category after evaluating specs, real-world buyer feedback, and long-term value.
            </p>
            <p className="text-muted-foreground mb-6 max-w-3xl">
              High-efficiency solar panels — those rated above 20% conversion efficiency — are a strong choice when space is limited or maximum energy density matters. We evaluate examples across PERC, TOPCon, HJT, and IBC technology using published specifications, warranty terms, compatibility information, and buyer-reported patterns. We do not claim hands-on laboratory testing unless a page explicitly documents it.
            </p>
            <AffiliateDisclosure />
            <TableOfContents items={TOC} />
          </div>

          <section id="comparison" className="mb-16">
            <h2 className="font-heading font-bold text-3xl mb-4">Quick Comparison Table</h2>
            {products.length > 0 ? (
              <ComparisonTable products={products} />
            ) : (
              <div className="bg-secondary/50 rounded-xl p-12 text-center">
                <p className="text-muted-foreground">Loading product data...</p>
              </div>
            )}
          </section>

          {products.length > 0 && (
            <section id="reviews" className="mb-16">
              <h2 className="font-heading font-bold text-3xl mb-8">Detailed Reviews by Category</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </section>
          )}

          <section id="how-we-picked" className="mb-16 max-w-3xl">
            <h2 className="font-heading font-bold text-2xl mb-6">How We Picked These Panels</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Every panel on this list was evaluated against a consistent set of criteria. We start with efficiency — only panels rated at 20% or higher qualify as "high-efficiency" for consumer applications. Below that threshold, the energy gains don't justify the premium price point in most real-world setups.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We also look at <strong>form factor suitability</strong> — a 400W rigid panel is a great RV rooftop panel but a terrible camping panel. Each product is evaluated within the context of its intended use case. A portable foldable panel rated at 100W and 23% efficiency is a better "portable camping panel" than a 200W rigid unit that weighs 25 lbs.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Connector standards matter</strong> because incompatible connectors waste money on adapters and create potential failure points. We favor panels with industry-standard MC4 connectors for fixed installations and Anderson Powerpole or XT60 for portable use.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Warranty length</strong> is a proxy for manufacturer confidence. A panel backed by a 25-year performance warranty is a fundamentally different product than one with a 1-year warranty, even if the specs on paper look similar. For permanent installations, we strongly favor panels with long-term performance guarantees.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We also factor in <strong>buyer feedback</strong> as a secondary signal. Repeated reports can reveal issues that specification sheets do not cover, such as shipping damage, connector durability, or moisture concerns. Buyer reports are treated as anecdotal evidence rather than controlled test results.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Finally, we do not accept sponsored placements, affiliate incentives to rank products higher, or manufacturer-provided test data. Prices change frequently on Amazon — we never hardcode prices. Use the "Check Price on Amazon" buttons to see current, live pricing.
            </p>

            <div className="mt-10" id="what-efficiency-means">
              <h2 className="font-heading font-bold text-2xl mb-6">What "High Efficiency" Actually Means for Buyers</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The word "efficiency" gets overused in solar marketing. Here's what it actually means for your purchase decision: a 22% efficiency panel produces 22 watts of electricity for every 100 watts of sunlight that hits it. A 17% efficiency panel produces 17 watts from the same 100 watts of sunlight.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                That 5-percentage-point difference is significant when you have limited surface area — like an RV roof, a backpack, or a van roof. A high-efficiency panel lets you produce more power from less space. But if space isn't a constraint, a lower-efficiency panel at a lower price point may deliver equal value.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The practical takeaway: prioritize efficiency when space is limited. For large ground mounts or open rooftops with room to add more panels, price-per-watt often matters more than efficiency percentage.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Also worth noting: efficiency ratings are measured under Standard Test Conditions (STC) — 25°C cell temperature, 1000 W/m² irradiance, and AM 1.5 solar spectrum. Real-world output is typically 10–25% lower due to temperature, partial shading, wiring losses, and non-optimal sun angles. A panel rated at 22% STC efficiency will realistically produce closer to 17–20% in outdoor conditions. All panels lose some efficiency at high temperatures — this is why HJT panels, which have a superior temperature coefficient, outperform PERC panels in hot climates despite sometimes having similar STC ratings.
              </p>
            </div>

            <div className="mt-10" id="technology">
              <h2 className="font-heading font-bold text-2xl mb-6">Which Panel Technology Should You Choose?</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Not all high-efficiency panels are created equal. The four main technologies you'll encounter are PERC, HJT, TOPCon, and IBC — each with distinct trade-offs.
              </p>
              <div className="space-y-4">
                <div className="bg-secondary/50 rounded-lg p-5">
                  <h3 className="font-heading font-semibold mb-2">PERC (Passivated Emitter Rear Cell)</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The most widely produced high-efficiency technology. PERC panels add a reflective layer on the back of the cell to bounce unused light back through the silicon, increasing efficiency by 1–2 percentage points over standard mono. They're now the baseline for quality solar panels, available in a wide range of wattages, and generally the most affordable of the high-efficiency options. Great all-around choice for most buyers.
                  </p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-5">
                  <h3 className="font-heading font-semibold mb-2">HJT (Heterojunction Technology)</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    HJT panels sandwich amorphous silicon layers around a crystalline silicon core. This design dramatically reduces recombination losses and gives HJT panels a much lower temperature coefficient — they lose less efficiency in the heat. In climates where summer temperatures regularly push panels above 50°C, HJT panels can outperform PERC panels significantly despite similar STC efficiency ratings. They also degrade more slowly over time. The downside: they cost more. Best for hot climates, long-term installations, and buyers who want maximum lifetime energy yield.
                  </p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-5">
                  <h3 className="font-heading font-semibold mb-2">TOPCon (Tunnel Oxide Passivated Contact)</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    TOPCon is the newest mainstream technology and is rapidly displacing standard PERC in manufacturing. It achieves higher efficiency than PERC through a passivated contact structure that reduces recombination at the back of the cell. TOPCon panels can exceed 22.5% efficiency at competitive prices, and they perform well at low-light conditions — useful in overcast climates. Increasingly the best choice when you want PERC pricing with HJT-adjacent performance.
                  </p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-5">
                  <h3 className="font-heading font-semibold mb-2">IBC (Interdigitated Back Contact)</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    IBC panels move all electrical contacts to the rear of the cell, eliminating shading from front-side busbars. The result is the highest conversion efficiency of any consumer silicon solar panel — regularly above 24%. SunPower (now Maxeon) pioneered this technology and it remains the gold standard for premium residential installations where space is limited and maximum yield per square foot matters. The price premium is real, but so is the performance advantage.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="related" className="border-t border-border pt-8">
            <h3 className="font-heading font-semibold mb-4">Related Guides</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: "Best Portable Solar Panels", path: "/best-portable-solar-panels" },
                { label: "Best Solar Panels for RV", path: "/best-solar-panels-for-rv" },
                { label: "Best for Solar Generators", path: "/best-solar-panels-for-solar-generators" },
                { label: "Bifacial Solar Panels", path: "/bifacial-solar-panels" },
                { label: "Flexible Solar Panels", path: "/flexible-solar-panels" },
                { label: "Solar Panel Calculator", path: "/solar-panel-calculator" },
              ].map((link) => (
                <Link key={link.path} to={link.path} className="px-4 py-2 bg-secondary rounded-lg text-sm font-medium hover:bg-accent hover:text-foreground transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
          </section>
        </div>
        <EmailCapture source="best-panels" />
      </div>
    </>
  );
}